import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles';
import i18n from '../../i18n'; // IMPORT i18n directly
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const ThemeContext = createContext();

// Base themes
const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: '#FFD700',
      dark: '#D4AF37',
      light: '#FFED85',
    },
    secondary: {
      main: '#C0C0C0',
    },
    background: {
      default: '#FFF8E1',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2d3748',
      secondary: '#718096',
    },
    success: {
      main: '#10B981',
      light: '#D1FAE5',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
    },
    error: {
      main: '#EF4444',
      light: '#FEE2E2',
    },
    info: {
      main: '#3B82F6',
      light: '#DBEAFE',
    },
    divider: alpha('#000000', 0.1),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',
    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
  ],
};

const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD700',
      dark: '#D4AF37',
      light: '#FFED85',
    },
    secondary: {
      main: '#C0C0C0',
    },
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#e2e8f0',
      secondary: '#a0aec0',
    },
    success: {
      main: '#10B981',
      light: '#064E3B',
    },
    warning: {
      main: '#F59E0B',
      light: '#78350F',
    },
    error: {
      main: '#EF4444',
      light: '#7F1D1D',
    },
    info: {
      main: '#3B82F6',
      light: '#1E3A8A',
    },
    divider: alpha('#FFFFFF', 0.1),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.3)',
    '0 4px 6px -1px rgba(0,0,0,0.4), 0 2px 4px -1px rgba(0,0,0,0.2)',
    '0 10px 15px -3px rgba(0,0,0,0.4), 0 4px 6px -2px rgba(0,0,0,0.2)',
    '0 20px 25px -5px rgba(0,0,0,0.4), 0 10px 10px -5px rgba(0,0,0,0.2)',
    '0 25px 50px -12px rgba(0,0,0,0.5)',
  ],
};

const createThemeWithDirection = (baseTheme, isRTL) => {
  return createTheme({
    ...baseTheme,
    direction: isRTL ? 'rtl' : 'ltr',
    typography: {
      ...baseTheme.typography,
      fontFamily: isRTL 
        ? '"Vazirmatn", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'
        : '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            direction: isRTL ? 'rtl' : 'ltr',
            fontFamily: isRTL 
              ? '"Vazirmatn", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'
              : '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            transition: 'background-color 0.3s ease',
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: isRTL ? '#2d2d2d' : '#f1f1f1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: '#FFD700',
            borderRadius: '4px',
            '&:hover': {
              background: '#D4AF37',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            direction: isRTL ? 'rtl' : 'ltr',
            transition: 'transform 0.3s ease-in-out',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            direction: isRTL ? 'rtl' : 'ltr',
            transition: 'all 0.3s ease',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.2s ease',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: 'all 0.3s ease',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 0',
          },
        },
      },
    },
  });
};

export function CustomThemeProvider({ children }) {
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('goldstore-theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('i18nextLng') || 'en';
  });

  const isRTL = language === 'fa' || language === 'ps';

  const theme = useMemo(() => {
    const baseTheme = darkMode ? darkTheme : lightTheme;
    return createThemeWithDirection(baseTheme, isRTL);
  }, [darkMode, isRTL]);

  useEffect(() => {
    localStorage.setItem('goldstore-theme', darkMode ? 'dark' : 'light');
    
    // Update meta theme color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', darkMode ? '#1a1a1a' : '#FFD700');
    }
  }, [darkMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('goldstore-theme')) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  const changeLanguage = useCallback(async (lang) => {
    try {
      console.log('Changing language to:', lang);
      await i18n.changeLanguage(lang); // Use imported i18n instance
      setLanguage(lang);
      localStorage.setItem('i18nextLng', lang);
      
      // Update moment locale
      const localeMap = {
        'en': 'en',
        'fa': 'fa',
        'ps': 'fa' // Pashto uses Persian calendar
      };
      const momentLocale = localeMap[lang] || 'en';
      moment.locale(momentLocale);
      
      // Update document attributes
      document.documentElement.setAttribute('lang', lang);
      document.documentElement.setAttribute('dir', lang === 'fa' || lang === 'ps' ? 'rtl' : 'ltr');
      
      console.log('Language changed successfully');
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  }, []);

  const value = useMemo(() => ({
    darkMode,
    toggleDarkMode,
    language,
    changeLanguage,
    isRTL,
    t
  }), [darkMode, toggleDarkMode, language, changeLanguage, isRTL, t]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within CustomThemeProvider');
  }
  return context;
};